<template>
    <g-form>
        <g-loader v-if="isLoading" />
        <div v-else>
            <g-base-box class="margin-bottom-large">
                <g-media-object>
                    <template #left>
                        <g-icon name="info-circle" type="solid" size="large" color="green-5" />
                    </template>
                    <template #default>
                        <g-typography
                            tag="p"
                            font-weight="semibold"
                            class="g-mb-3"
                            :value="CONTENT_METRIC_SCREENSHOT_INFO_HEADER_TEXT"
                        />

                        <g-typography tag="p" size="small">
                            Navigate to your posted {{ taskType }} and go to the insights section that appears. It may
                            take several screen shots to capture all the metrics, that’s okay! You can upload as many as
                            you need.
                        </g-typography>
                    </template>
                </g-media-object>
            </g-base-box>

            <g-form class="margin-bottom-normal">
                <g-loader v-if="isFileDeleting || isFileUploading" type="absolute" />
                <g-form-item required>
                    <g-typography
                        v-if="hasUploadedFiles"
                        tag="p"
                        font-weight="semibold"
                        class="margin-bottom-normal"
                        value="Uploaded Screenshots"
                    />

                    <div v-for="(file, i) in getFilesWithUrls()" :key="i" class="margin-bottom-normal">
                        <activation-file :file="file" :deletable="true" @file-deleted="handleFileDeleted" />
                    </div>

                    <g-row :gutter="16">
                        <g-column :span="hasUploadedFiles ? 12 : 24">
                            <g-upload
                                :headers="uploadHeaders"
                                :action="uploadScreenshotUrl"
                                :multiple="true"
                                :show-upload-list="false"
                                :before-upload="handleBeforeUploadValidation"
                                @change="handleFileChange"
                            >
                                <g-button
                                    class="full-width-mobile"
                                    shape="round"
                                    theme="live"
                                    size="large"
                                    :type="hasUploadedFiles ? 'secondary' : 'primary'"
                                    block
                                    :disabled="isLoading || isSubmitting || isFileUploading || isFileDeleting"
                                    :loading="isFileUploading"
                                >
                                    <g-icon v-if="!isFileUploading" name="upload" />
                                    Upload {{ hasUploadedFiles ? 'More' : 'Screenshots' }}
                                </g-button>
                            </g-upload>
                        </g-column>
                        <g-column v-if="hasUploadedFiles" span="12">
                            <g-button
                                shape="round"
                                theme="live"
                                block
                                type="primary"
                                size="large"
                                :loading="isSubmitting"
                                :disabled="isLoading || isSubmitting"
                                @click="handleSubmit"
                            >
                                Submit Metrics
                            </g-button>
                        </g-column>
                    </g-row>

                    <g-typography
                        tag="p"
                        color="grey-5"
                        size="small"
                        :value="CONTENT_METRIC_SCREENSHOT_ACCEPTED_FILE_TYPES_TEXT"
                    />
                </g-form-item>
            </g-form>
        </div>
    </g-form>
</template>

<script>
    import { toRefs, computed } from 'vue-demi';
    import {
        CONTENT_METRIC_SCREENSHOT_INFO_HEADER_TEXT,
        CONTENT_METRIC_SCREENSHOT_ACCEPTED_FILE_TYPES_TEXT
    } from '../../../../../constants/activation-content-metrics';
    import { NETWORK_LABEL_MAP } from '../../../../../constants/networks';
    import ActivationFile from '../../../../library/ActivationFile.vue';
    import { useContactStore } from '../../../../../../stores';
    import useContentMetrics from '../../../../../../src/composables/use-content-metrics';

    export default {
        name: 'ActivationContentMetricsScreenshot',
        components: { ActivationFile },
        props: {
            content: {
                type: Object,
                default: null,
                required: true
            },
            task: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isSubmitting: {
                type: Boolean,
                default: false
            },
            uploadScreenshotUrl: {
                type: String,
                default: ''
            },
            uploadHeaders: {
                type: Object,
                default: null
            },
            uploadedFiles: {
                type: Array,
                default: null
            },
            isFileUploading: {
                type: Boolean,
                default: false
            },
            isFileDeleting: {
                type: Boolean,
                default: false
            },
            handleBeforeUploadValidation: {
                type: Function,
                default: () => {}
            }
        },

        setup(props, { emit }) {
            const contact = useContactStore().data;
            const { uploadedFiles, content, task } = toRefs(props);
            const { getScreenshotFilesWithUrls } = useContentMetrics();

            const getFilesWithUrls = () => {
                return getScreenshotFilesWithUrls(content.value?.id, contact, uploadedFiles.value);
            };

            const handleFileChange = (info) => {
                emit('file-change', info);
            };

            const getPayload = () => {
                return { files: getFilesWithUrls() };
            };

            const hasUploadedFiles = computed(() => uploadedFiles.value?.length > 0);
            const isReady = computed(() => hasUploadedFiles.value);
            const taskType = computed(() => NETWORK_LABEL_MAP[task.value?.type]);

            const handleSubmit = async () => {
                if (!isReady.value) {
                    return;
                }

                emit('screenshots-submitted', getPayload());
            };

            const handleFileDeleted = async (fileName) => {
                emit('file-deleted', fileName);
            };

            return {
                NETWORK_LABEL_MAP,
                CONTENT_METRIC_SCREENSHOT_INFO_HEADER_TEXT,
                CONTENT_METRIC_SCREENSHOT_ACCEPTED_FILE_TYPES_TEXT,
                hasUploadedFiles,
                isReady,
                taskType,
                getFilesWithUrls,
                handleFileDeleted,
                handleFileChange,
                handleSubmit
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    ::v-deep .ant-upload.ant-upload-select {
        display: block;
        margin-bottom: $g-spacing-5;
    }

    ::v-deep .ant-form-item {
        padding-bottom: $g-spacing-5;
        margin-bottom: 0;
    }
</style>
